// Pure decisions for locking the session to a selected game (issue 410).
//
// A player who opens a game should have neutral navigation (the TRANSIGO
// logo, a bare "/" visit, post-login landing) return them to that game's
// dashboard instead of the marketing home page or the games list. Switching
// games is a deliberate act via the "Games" nav link, not something that
// happens as a side effect of browsing.

// Route names whose :id param selects "the game I'm playing". Deliberately
// excludes admin routes (admin-audit) and the dev map harness — an admin
// auditing a game, or a developer poking at the map harness, must not have
// their session re-locked to it.
export const SCENARIO_ROUTE_NAMES = new Set([
  'dashboard',
  'finance',
  'vehicle-detail',
  'company',
  'notifications'
])

// Given a vue-router route object, returns the scenario id that visiting it
// should select, or null if this route doesn't participate in selection.
export function resolveSelectionFromRoute(route) {
  if (!route?.name || !SCENARIO_ROUTE_NAMES.has(route.name)) return null
  return route.params?.id ?? null
}

// Where neutral navigation ("/", post-login, the logo) should land given the
// currently selected scenario id.
export function resolveHomeTarget(selectedId) {
  return selectedId ? `/scenarios/${selectedId}` : '/'
}

// Trailing "/ Section" label shown after the game name in the nav breadcrumb
// (issue 412) — null on the dashboard itself, which needs no trailing segment.
const SECTION_LABELS = {
  finance: 'Finance',
  'vehicle-detail': 'Vehicle',
  company: 'Company',
  notifications: 'Notifications'
}

// Whether a route's nav bar shows the "/ Game" breadcrumb slot, and what (if
// any) "/ Section" label trails it. Reuses SCENARIO_ROUTE_NAMES so this stays
// in lockstep with the routes that participate in session-lock selection.
export function navContext(route) {
  if (!route?.name || !SCENARIO_ROUTE_NAMES.has(route.name)) {
    return { visible: false, sectionLabel: null }
  }
  return { visible: true, sectionLabel: SECTION_LABELS[route.name] ?? null }
}

// Status -> nav breadcrumb dot color token (issue 412). Never brass — the dot
// is status, and --color-accent is reserved for actions (Charter rule 1).
const STATUS_DOT_TOKENS = {
  ACTIVE: 'var(--color-success)',
  LOBBY: 'var(--color-warning)',
  COMPLETED: 'var(--color-border-strong)'
}

export function statusDotToken(status) {
  return STATUS_DOT_TOKENS[status] ?? null
}

// Human-readable status text for the visually-hidden a11y label accompanying
// the dot — the dot's color must never be the only carrier of status.
const STATUS_LABELS = {
  ACTIVE: 'Active',
  LOBBY: 'Lobby',
  COMPLETED: 'Completed'
}

export function statusLabel(status) {
  if (status == null) return null
  return STATUS_LABELS[status] ?? status
}
